import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import ChartIdentifiersEditor from "../ChartIdentifiersEditor";

interface Chart {
  identifiers: {
    identifier: string;
    overriddenTitle?: string;
  }[];
  chartName: string;
}

interface DataTableProps {
  charts: Chart[];
  onDataUpdate: (updatedData: Chart[]) => void;
  handleDatasetChange: (
    rowIndex: number,
    selectedIdentifiers: { identifier: string; overriddenTitle?: string }[]
  ) => void;
  handleAddChart: () => void;
}

const DataTableComponent = ({
  charts,
  onDataUpdate,
  handleDatasetChange,
  handleAddChart,
}: DataTableProps) => {
  const [data, setData] = useState<Chart[]>([]);
  const [pieChartEditorDialogVisibility, setPieChartEditorDialogVisibility] = useState(false);
  const [chartToEdit, setChartToEdit] = useState<Chart | null>(null);
  const [editChartIndex, setEditChartIndex] = useState<number | null>(null);

  useEffect(() => {
    setData(charts);
  }, [charts]);

  const renderNames = (rowData: Chart) => {
    return <span>{rowData.chartName}</span>;
  };

  const renderIdentifiers = (rowData: Chart, rowIndex: number) => {
    return (
      <div className="p-0">
        <DatasetMultiSelect
          value={rowData.identifiers.map(id => id.identifier) || []}
          onChange={(selectedIdentifiers: string[]) => {
            const existingIdentifiersMap = new Map(
              rowData.identifiers.map(id => [id.identifier, id.overriddenTitle])
            );

            const updatedIdentifiers = selectedIdentifiers.map(identifier => ({
              identifier,
              overriddenTitle: existingIdentifiersMap.get(identifier) || "",
            }));

            handleDatasetChange(rowIndex, updatedIdentifiers);
            const updatedData = [...data];
            updatedData[rowIndex].identifiers = updatedIdentifiers;
            setData(updatedData);
            onDataUpdate(updatedData);
          }}
        />
      </div>
    );
  };
  const onRowEditComplete = (e: any): void => {
    const updatedData = [...data];
    const { index, newData } = e;
    updatedData[index] = { ...updatedData[index], ...newData };
    setData(updatedData);
    onDataUpdate(updatedData);
  };

  const textEditor = (options: any) => {
    return (
      <InputText type="text" value={options.value} onChange={e => options.editorCallback(e.target.value)} />
    );
  };

  const handleDeleteChart = (rowData: Chart): void => {
    const updatedData = data.filter(item => item !== rowData);
    setData(updatedData);
    onDataUpdate(updatedData);
  };
  const handleRowReorderEvt = (e: { from: any; to: any }) => {
    const reorderedData = [...data];
    const [removed] = reorderedData.splice(e.from, 1);
    reorderedData.splice(e.to, 0, removed);
    setData(reorderedData);
    onDataUpdate(reorderedData);
  };

  const tableHeader = () => {
    return (
      <div className="flex w-full justify-content-end">
        <Button
          type="button"
          icon="pi pi-plus"
          tooltip={T("action.add-group-or-dataset")}
          className="p-button-rounded p-button-success p-button-text p-ml-2"
          style={{ fontSize: "0.8rem" }}
          onClick={() => handleAddChart()}
        />
      </div>
    );
  };

  const renderIdentifiersEditButton = (rowData: Chart, options: any) => {
    return (
      <Button
        icon="pi pi-pencil"
        type="button"
        className="p-button-secondary"
        tooltip={T("action.edit-identifiers")}
        onClick={() => {
          setChartToEdit(rowData);
          setEditChartIndex(options.rowIndex);
          setPieChartEditorDialogVisibility(true);
        }}
      />
    );
  };

  const renderChartDeleteButton = (rowData: Chart) => {
    return (
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-outlined p-button-danger"
        onClick={() => handleDeleteChart(rowData)}
      />
    );
  };
  return (
    <div className="card p-fluid w-full">
      <h5>{T("word.charts")}</h5>
      <DataTable
        header={tableHeader}
        className="overflow-scroll"
        scrollHeight="300px"
        value={data}
        editMode="row"
        dataKey="id"
        onRowEditComplete={onRowEditComplete}
        responsiveLayout="scroll"
        size="small"
        onRowReorder={handleRowReorderEvt}
        reorderableRows
      >
        <Column rowReorder style={{ width: "3%" }} />
        <Column
          field="chartName"
          editor={options => textEditor(options)}
          header={T("word.chart-name")}
          body={renderNames}
          style={{ width: "10%" }}
        />
        <Column rowEditor style={{ textAlign: "right", width: "5%" }} />
        <Column
          field="identifiers"
          header={T("word.identifiers")}
          headerStyle={{ paddingLeft: "1rem" }}
          body={(rowData, options) => renderIdentifiers(rowData, options.rowIndex)}
          style={{ width: "50%" }}
        />
        <Column
          style={{ width: "5%" }}
          align="center"
          body={(rowData, options) => renderIdentifiersEditButton(rowData, options)}
        />
        <Column body={rowData => renderChartDeleteButton(rowData)} style={{ width: "5%" }} />
      </DataTable>
      <Dialog
        onHide={() => setPieChartEditorDialogVisibility(false)}
        visible={pieChartEditorDialogVisibility}
        header={T("action.edit-identifiers")}
        style={{ width: "min(100vw, 1000px)", height: "400px" }}
      >
        {chartToEdit && editChartIndex !== null && (
          <ChartIdentifiersEditor
            currentConfig={chartToEdit.identifiers}
            setCurrentConfig={(updatedConfig: { identifier: string; overriddenTitle?: string }[]) => {
              const updatedData = [...data];
              updatedData[editChartIndex].identifiers = updatedConfig;
              setData(updatedData);
              onDataUpdate(updatedData);
            }}
            chartType="PIE"
          />
        )}
      </Dialog>
    </div>
  );
};

export default DataTableComponent;
